import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import ProxySessionDialog from '../components/superadmin/ProxySessionDialog';
import { authAPI, employeeAPI, workspaceAdminAPI, setAccessToken, clearAccessToken, getStoredAccessToken } from '../services/api';

const AuthContext = createContext(null);
let restoreSessionInFlight = null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Thrown when the operator dismisses the consent dialog. Callers treat it as
// "nothing happened" rather than as a failure worth showing an error for.
export const PROXY_CANCELLED = 'PROXY_CANCELLED';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // The consent gate lives here, not in the tenants table, because proxy
  // sessions are started from several places — the tenants list, the tenant
  // switcher, the org master page. A prompt on only one of them is a prompt
  // with a way around it.
  const [proxyPrompt, setProxyPrompt] = useState(null);
  const proxyPromptResolver = useRef(null);

  const askForProxyReason = (request) =>
    new Promise((resolve, reject) => {
      proxyPromptResolver.current = { resolve, reject };
      setProxyPrompt(request);
    });

  const closeProxyPrompt = () => {
    proxyPromptResolver.current = null;
    setProxyPrompt(null);
  };

  const cancelProxyPrompt = () => {
    const pending = proxyPromptResolver.current;
    closeProxyPrompt();
    if (pending) {
      const error = new Error('Proxy session cancelled');
      error.code = PROXY_CANCELLED;
      pending.reject(error);
    }
  };
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const buildAuthUser = (data, fallbackEmail = null) => ({
    email: data?.email || fallbackEmail,
    role: data?.role || null,
    employeeId: data?.employee_id || null,
    tenantId: data?.tenant_id || null,
    parentTenantId: data?.parent_tenant_id || null,
    parentTenantSlug: data?.parent_tenant_slug || null,
    parentTenantName: data?.parent_tenant_name || null,
    tenantSlug: (() => {
      // Remembered so the login screen can paint this workspace's brand next
      // time, before there is a session to read it from. See usePublicBranding.
      const slug = data?.tenant_slug || null;
      try {
        if (slug) window.localStorage.setItem('hrms.lastWorkspaceSlug', slug);
      } catch { /* private browsing; the login screen just stays unbranded */ }
      return slug;
    })(),
    tenantName: data?.tenant_name || null,
    emailVerified: Boolean(data?.email_verified),
    modules: Array.isArray(data?.modules) ? data.modules : [],
    moduleAccess: Array.isArray(data?.module_access) ? data.module_access : [],
    // null, not [], when the role carries no menu restriction — an empty array
    // here would hide every menu instead of none.
    menuAccess: Array.isArray(data?.menu_access) ? data.menu_access : null,
    menuDenied: Array.isArray(data?.menu_denied) ? data.menu_denied : [],
    permissions: Array.isArray(data?.permissions) ? data.permissions : [],
    rolePermissions: Array.isArray(data?.role_permissions) ? data.role_permissions : [],
    rolePermissionsMap: data?.role_permissions_map || {},
    limits: data?.limits || {},
    permissionGroups: Array.isArray(data?.permission_groups) ? data.permission_groups : [],
    canCreateChildTenants: Boolean(data?.can_create_child_tenants),
    subscriptionPlanCode: data?.subscription_plan_code || null,
    subscriptionPlanName: data?.subscription_plan_name || null,
    isSuperadmin: data?.role === 'superadmin',
    isProxySession: Boolean(data?.is_proxy_session),
    proxySourceTenantSlug: data?.proxy_source_tenant_slug || null,
    canReturnToSuperadmin: Boolean(data?.is_proxy_session && data?.proxy_source_tenant_slug),
    mfaRequired: Boolean(data?.mfa_required),
    mfaEnrolled: Boolean(data?.mfa_enrolled),
    name: data?.name || null,
    firstName: data?.first_name || null,
    lastName: data?.last_name || null,
    profilePhotoUrl: data?.profile_photo_url || data?.profilePhotoUrl || null,
  });

  const hydrateEmployeeProfile = async (baseUser) => {
    const role = String(baseUser?.role || '').toLowerCase();
    if (!baseUser?.email || !role || role === 'superadmin') return baseUser;
    try {
      const response = await employeeAPI.getMyProfile();
      const firstName = response?.data?.first_name || null;
      const lastName = response?.data?.last_name || null;
      const name = String([firstName, lastName].filter(Boolean).join(' ')).trim() || null;
      const profilePhotoUrl = response?.data?.profile_photo_url || null;
      return {
        ...baseUser,
        name: baseUser?.name || name,
        firstName: baseUser?.firstName || firstName,
        lastName: baseUser?.lastName || lastName,
        profilePhotoUrl: baseUser?.profilePhotoUrl || profilePhotoUrl,
      };
    } catch (error) {
      return baseUser;
    }
  };

  useEffect(() => {
    const restoreSession = async () => {
      const startRestore = async () => {
        const response = await authAPI.me();
        const baseUser = buildAuthUser(response.data, response.data?.email);
        const hydrated = await hydrateEmployeeProfile(baseUser);
        return hydrated;
      };

      if (!getStoredAccessToken()) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        restoreSessionInFlight = restoreSessionInFlight || startRestore().finally(() => {
          restoreSessionInFlight = null;
        });
        const hydrated = await restoreSessionInFlight;
        setUser(hydrated);
      } catch (error) {
        clearAccessToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
      const baseUser = buildAuthUser(response.data, email);
      setUser(baseUser);
      const hydrated = await hydrateEmployeeProfile(baseUser);
      setUser(hydrated);
    }
    return response.data;
  };

  const verifyMfaChallenge = async (challengeToken, code, fallbackEmail = null) => {
    const response = await authAPI.verifyMfa(challengeToken, code);
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
      const baseUser = buildAuthUser(response.data, fallbackEmail || response?.data?.email);
      setUser(baseUser);
      const hydrated = await hydrateEmployeeProfile(baseUser);
      setUser(hydrated);
    }
    return response.data;
  };

  // Same session builder on the server as password login, so the token, refresh
  // cookie and capability payload are identical whichever way the user signed in.
  const loginWithOtp = async (identifier, code) => {
    const response = await authAPI.verifyLoginOtp(identifier, code);
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
      const baseUser = buildAuthUser(response.data, response?.data?.email || identifier);
      setUser(baseUser);
      const hydrated = await hydrateEmployeeProfile(baseUser);
      setUser(hydrated);
    }
    return response.data;
  };

  const proxyLogin = async (tenantSelector, options = null) => {
    let response;
    // No reason supplied means this came from a plain "switch to tenant"
    // action; ask for one rather than letting it through unrecorded.
    const consent = options || (await askForProxyReason({
      tenantSlug: typeof tenantSelector === 'string' ? tenantSelector : String(tenantSelector || ''),
      tenantName: null,
      actorEmail: user?.email || null,
    }));
    try {
      response = await workspaceAdminAPI.proxyLogin(tenantSelector, consent);
    } catch (error) {
      const detail = String(error?.response?.data?.detail || '');
      if (detail.includes('Already in the selected tenant')) {
        // Idempotent fallback: stay on current authenticated session.
        const meResponse = await authAPI.me();
        const currentUser = buildAuthUser(meResponse.data, meResponse.data?.email || user?.email);
        setUser(currentUser);
        return {
          role: currentUser.role,
          tenant_slug: currentUser.tenantSlug,
          tenant_id: currentUser.tenantId,
          is_proxy_session: currentUser.isProxySession,
          proxy_source_tenant_slug: currentUser.proxySourceTenantSlug,
        };
      }
      throw error;
    }
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
    }
    const requestedTenant = String(tenantSelector || '').toLowerCase();
    const immediateUser = buildAuthUser(response.data, response.data?.email || user?.email);
    setUser(immediateUser);
    const hydratedImmediate = await hydrateEmployeeProfile(immediateUser);
    setUser(hydratedImmediate);

    let verifiedUser = null;
    let lastVerifyError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const meResponse = await authAPI.me();
        verifiedUser = buildAuthUser(meResponse.data, meResponse.data?.email);
        const verifiedTenant = String(verifiedUser?.tenantSlug || '').toLowerCase();
        if (!requestedTenant || verifiedTenant === requestedTenant) {
          break;
        }
      } catch (error) {
        lastVerifyError = error;
      }
      await delay(150);
    }

    const verifiedTenant = String(verifiedUser?.tenantSlug || '').toLowerCase();
    if (verifiedUser && (!requestedTenant || verifiedTenant === requestedTenant)) {
      setUser(verifiedUser);
      const hydratedVerified = await hydrateEmployeeProfile(verifiedUser);
      setUser(hydratedVerified);
      return {
        ...response.data,
        role: verifiedUser.role,
        tenant_slug: verifiedUser.tenantSlug,
        tenant_id: verifiedUser.tenantId,
      };
    }

    const immediateTenant = String(immediateUser?.tenantSlug || '').toLowerCase();
    if (requestedTenant && immediateTenant === requestedTenant) {
      return {
        ...response.data,
        role: immediateUser.role,
        tenant_slug: immediateUser.tenantSlug,
        tenant_id: immediateUser.tenantId,
      };
    }

    throw new Error(
      `Proxy verification failed. Requested=${requestedTenant || 'n/a'}, Immediate=${immediateTenant || 'n/a'}, Verified=${verifiedTenant || 'n/a'}, VerifyError=${lastVerifyError?.message || 'none'}`
    );
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Clear local auth state even if the server session is already gone.
    }
    clearAccessToken();
    setUser(null);
  };

  /// Opens another location of the same organisation.
  ///
  /// Separate from proxyLogin, which asks for a reason code and a justification
  /// before it will move. That gate belongs to a platform operator entering a
  /// customer's workspace; requiring it of an HR admin opening their own branch
  /// every morning would only teach them to type anything into the box.
  const switchLocation = async (tenantSlug) => {
    const response = await workspaceAdminAPI.switchLocation(tenantSlug);
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
    }
    // Re-read from the server rather than trusting the response body: the
    // session that matters is the one the token actually carries, and the
    // banner and the sidebar both key off it.
    const meResponse = await authAPI.me();
    const nextUser = buildAuthUser(meResponse.data, meResponse.data?.email);
    setUser(nextUser);
    const hydrated = await hydrateEmployeeProfile(nextUser);
    setUser(hydrated);
    return {
      ...response.data,
      role: nextUser.role,
      tenant_slug: nextUser.tenantSlug,
      tenant_id: nextUser.tenantId,
    };
  };

  const exitProxy = async () => {
    const response = await workspaceAdminAPI.proxyExit();
    if (response?.data?.access_token) {
      setAccessToken(response.data.access_token);
    }
    const meResponse = await authAPI.me();
    const nextUser = buildAuthUser(meResponse.data, meResponse.data?.email);
    setUser(nextUser);
    const hydrated = await hydrateEmployeeProfile(nextUser);
    setUser(hydrated);
    return {
      ...response.data,
      role: nextUser.role,
      tenant_slug: nextUser.tenantSlug,
      tenant_id: nextUser.tenantId,
    };
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyMfaChallenge, loginWithOtp, logout, proxyLogin, switchLocation, exitProxy, loading }}>
      {children}
      <ProxySessionDialog
        open={Boolean(proxyPrompt)}
        request={proxyPrompt}
        onCancel={cancelProxyPrompt}
        onConfirm={(consent) => {
          const pending = proxyPromptResolver.current;
          closeProxyPrompt();
          pending?.resolve(consent);
        }}
      />
    </AuthContext.Provider>
  );
};
